import { getWorkflowPercComplete } from 'features/libra/helpers/getWorkflowPercComplete'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useLibraWorkflows } from 'features/libra/ticket/libraTicketsApi'
import {
  ELIGIBILITY_STATUS,
  type EligibilityStatus,
} from 'features/libra/types/eligibility'
import reduce from 'lodash/reduce'
import uniq from 'lodash/uniq'
import { useMemo } from 'react'

import { AssetStatus, getAssetStatus } from './getAssetStatus'

export type AssetStats = {
  unitId: string
  ticketCount: number
  avgProgress: number
  status: AssetStatus
  eligibility: EligibilityStatus
}

function emptyAssetStats(unitId: string): AssetStats {
  return {
    unitId,
    ticketCount: 0,
    avgProgress: 0,
    status: AssetStatus.NotStarted,
    eligibility: ELIGIBILITY_STATUS.UNCLEAR,
  }
}

export const useAssetStats = (
  tickets: ReadonlyArray<LibraTicket>,
  getAssetEligibility: (unitId: string) => EligibilityStatus
): {
  assetStats: ReadonlyArray<AssetStats>
  isLoading: boolean
} => {
  // `tickets` should be latest versions only (one ticket per inspection chain).
  const workflowIds = useMemo(
    () =>
      uniq(
        tickets
          .map((ticket) => ticket.workflowId)
          .filter((id): id is string => id !== undefined)
      ),
    [tickets]
  )

  const { libraWorkflows, isLoading: workflowsLoading } = useLibraWorkflows(
    useMemo(() => ({ ids: workflowIds }), [workflowIds])
  )

  const assetStats = useMemo<ReadonlyArray<AssetStats>>(() => {
    const workflowMap = new Map(
      libraWorkflows.map((workflow) => [workflow.id, workflow])
    )
    const statsMap = reduce(
      tickets,
      (acc, ticket) => {
        const unitId = ticket.tags.unit
        if (!unitId) return acc

        const stats = acc.get(unitId) ?? emptyAssetStats(unitId)
        stats.ticketCount++

        const workflow = workflowMap.get(ticket.workflowId)
        stats.avgProgress += getWorkflowPercComplete(ticket, workflow)

        acc.set(unitId, stats)
        return acc
      },
      new Map<string, AssetStats>()
    )

    statsMap.forEach((stats, unitId) => {
      if (stats.ticketCount > 0) {
        stats.avgProgress = Math.round(stats.avgProgress / stats.ticketCount)
      }

      const unitTickets = tickets.filter(
        (ticket) => ticket.tags.unit === unitId
      )
      stats.status = getAssetStatus(unitTickets, workflowMap)
      stats.eligibility = getAssetEligibility(unitId)
    })

    return Array.from(statsMap.values())
  }, [libraWorkflows, tickets, getAssetEligibility])

  return { assetStats, isLoading: workflowsLoading }
}
